import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button, Divider, Segmented, Select, Tabs, Tooltip } from "antd";
import { ReloadOutlined } from "@ant-design/icons";

import { apiGetPerformanceStats } from "api/apiTrades";
import PerformanceStatsCards from "components/Home/PerformanceStatsCards";
import Strategies from "components/Strategies/Strategies";
import OpenPositions from "components/OpenPositions/OpenPositions";
import TradesHistory from "components/TradesHistory/TradesHistory";
import OptionsStrategies from "components/OptionsStrategies/OptionsStrategies";
import OptionTradesTable from "components/OptionsStrategies/OptionTradesTable";
import OrdersStatus from "components/OrdersStatus/OrdersStatus";
import {
  IBAccountSection,
  useIbAccountDetails,
} from "components/IBIntergration/IBIntergration";
import { selectAutomationAllowed } from "store/slices/main";
import PendingActionsBar from "./PendingActionsBar";
import { TradingRefreshContext } from "./tradingRefresh";
import {
  PERFORMANCE_PERIODS,
  findPerformancePeriod,
  periodStartDate,
} from "utils/performancePeriods";

import "./Trading.scss";

// value -> the performance-stats `instrument`
const INSTRUMENTS = [
  { label: "Stocks", value: "stocks", api: "stock" },
  { label: "Options", value: "options", api: "option" },
  { label: "All", value: "all", api: "all" },
];


const TAB_TITLES = {
  strategies: "Strategies",
  positions: "Open Positions",
  history: "Trades History",
  orders: "Broker Orders",
  ibkr: "Interactive Brokers",
};

const INSTRUMENT_TITLES = { stocks: "", options: "Options ", all: "All " };

// One page for strategies, open positions and trades history. The switch at
// the top picks stocks, options or all (positions and history of both; the
// strategies are per instrument). Instrument, tab and KPI period are kept in
// the URL (?instrument=options&tab=positions&period=ytd) so links and the old
// pages' redirects land on the right view; &strategyId= opens a strategy.
const Trading = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const instrument =
    INSTRUMENTS.find((item) => item.value === searchParams.get("instrument"))
      ?.value || "stocks";
  const automationAllowed = useSelector(selectAutomationAllowed);
  const [refreshKey, setRefreshKey] = useState(0);
  // like Home: the Interactive Brokers tab only while IBKR is connected
  const ibAccount = useIbAccountDetails({
    refreshKey,
    enabled: !!automationAllowed,
  });
  const ibkrConnected =
    automationAllowed && ibAccount.ibAccountDetails?.connected === true;
  const requestedTab = TAB_TITLES[searchParams.get("tab")]
    ? searchParams.get("tab")
    : "strategies";
  // "all" has no Strategies tab; Broker Orders only with automation,
  // Interactive Brokers only while connected
  const tab =
    (instrument === "all" && requestedTab === "strategies") ||
    (requestedTab === "orders" && !automationAllowed) ||
    (requestedTab === "ibkr" && !ibkrConnected)
      ? "positions"
      : requestedTab;
  const period = findPerformancePeriod(searchParams.get("period"));

  const accountType = useSelector((state) => state.main.accountType);
  const [performanceStats, setPerformanceStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  // each instrument's tabs mount on first view and then stay mounted, so
  // switching back doesn't refetch (the options table asks the IBKR gateway
  // for live ATM quotes on mount)
  const [visited, setVisited] = useState([instrument]);

  const updateParams = useCallback(
    (changes) =>
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        return next;
      }),
    [setSearchParams],
  );

  const notifyChanged = useCallback(() => setRefreshKey((key) => key + 1), []);
  const openStrategy = useCallback(
    (strategyId, strategyInstrument) =>
      updateParams({
        instrument: strategyInstrument,
        tab: "strategies",
        strategyId,
      }),
    [updateParams],
  );
  const tradingContext = useMemo(
    () => ({ refreshKey, notifyChanged, openStrategy }),
    [refreshKey, notifyChanged, openStrategy],
  );

  // a late answer for the previous instrument/account/period must not land
  const statsRunId = useRef(0);

  const getPerformanceStats = async () => {
    const runId = ++statsRunId.current;
    setIsLoadingStats(true);
    try {
      const stats = await apiGetPerformanceStats(
        accountType,
        INSTRUMENTS.find((item) => item.value === instrument).api,
        periodStartDate(period),
      );
      if (runId === statsRunId.current) setPerformanceStats(stats);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  useEffect(() => {
    setPerformanceStats(null);
  }, [accountType, instrument, period.value]);

  useEffect(() => {
    getPerformanceStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountType, instrument, period.value, refreshKey]);

  useEffect(() => {
    setVisited((prev) =>
      prev.includes(instrument) ? prev : [...prev, instrument],
    );
  }, [instrument]);

  useEffect(() => {
    document.title = `${INSTRUMENT_TITLES[instrument]}${TAB_TITLES[tab]} | Dindicator Dashboard`;
  }, [tab, instrument]);

  const positionsLabel = performanceStats
    ? `Open Positions (${performanceStats.activePositions || 0})`
    : "Open Positions";

  // AutoTrader's orders at IBKR (of this view's instrument) and the IBKR
  // account (the same in every view)
  const ordersTab = (view) =>
    automationAllowed
      ? [
          {
            key: "orders",
            label: "Broker Orders",
            children: (
              <OrdersStatus
                embedded
                tradeType={
                  { stocks: "stock", options: "option" }[view] || undefined
                }
              />
            ),
          },
          ...(ibkrConnected
            ? [
                {
                  key: "ibkr",
                  label: "Interactive Brokers",
                  children: <IBAccountSection account={ibAccount} embedded />,
                },
              ]
            : []),
        ]
      : [];

  const tabItemsFor = (view) => {
    if (view === "all") {
      return [
        {
          key: "positions",
          label: positionsLabel,
          children: (
            <>
              <h3 className="trading-section-title">Stocks</h3>
              <OpenPositions />
              <h3 className="trading-section-title">Options</h3>
              <OptionTradesTable view="open" />
            </>
          ),
        },
        {
          key: "history",
          label: "History",
          children: (
            <>
              <h3 className="trading-section-title">Stocks</h3>
              <TradesHistory />
              <h3 className="trading-section-title">Options</h3>
              <OptionTradesTable view="closed" />
            </>
          ),
        },
        ...ordersTab(view),
      ];
    }
    const isOptions = view === "options";
    return [
      {
        key: "strategies",
        label: "Strategies",
        children: isOptions ? <OptionsStrategies /> : <Strategies />,
      },
      {
        key: "positions",
        label: positionsLabel,
        children: isOptions ? (
          <OptionTradesTable view="open" />
        ) : (
          <OpenPositions />
        ),
      },
      {
        key: "history",
        label: "History",
        children: isOptions ? (
          <OptionTradesTable view="closed" />
        ) : (
          <TradesHistory />
        ),
      },
      ...ordersTab(view),
    ];
  };

  return (
    <TradingRefreshContext.Provider value={tradingContext}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1">
          <Divider orientation="left">Trading</Divider>
        </div>
        <Segmented
          options={INSTRUMENTS}
          value={instrument}
          onChange={(value) => updateParams({ instrument: value })}
        />
        <Select
          value={period.value}
          onChange={(value) => updateParams({ period: value })}
          options={PERFORMANCE_PERIODS.map(({ value, label }) => ({
            value,
            label,
          }))}
          style={{ width: 140 }}
        />
        <Tooltip title="Reload the KPIs and every tab (not the live option quotes)">
          <Button
            shape="circle"
            icon={<ReloadOutlined />}
            onClick={notifyChanged}
            loading={isLoadingStats}
          />
        </Tooltip>
      </div>

      <PerformanceStatsCards
        performanceStats={performanceStats}
        compact
        periodLabel={period.label}
      />
      {instrument !== "stocks" && (
        <p className="trading-note">
          Option P&L uses the prices you confirmed (or the 15:50 contract price
          for open positions), not broker fills.
        </p>
      )}

      <PendingActionsBar />

      {visited.map((view) => (
        <div key={view} style={{ display: view === instrument ? "" : "none" }}>
          <Tabs
            activeKey={tab}
            onChange={(value) => updateParams({ tab: value })}
            items={tabItemsFor(view)}
          />
        </div>
      ))}
    </TradingRefreshContext.Provider>
  );
};

export default Trading;
